import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Tree.module.css";
import BinaryTreeEngine from "../TreeEngine/BinaryTreeEngine";

const BinaryTreeWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [traversal, setTraversal] = useState<string | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<number[]>([]);
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [traversalComplete, setTraversalComplete] = useState(false);
  const [key, setKey] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);


  const handleGenerate = () => {
    setKey((prev) => prev + 1);
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodes([]);
    setTraversalComplete(false);
  };

  const handleReset = () => {
    setResetTrigger((prev) => prev + 1);
  };

  const handleTraversal = (type: string) => {
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodes([]);
    setTraversalComplete(false);
    setTimeout(() => setTraversal(type), 100);
  };

  return (
    <div className={styles.treeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Binary Tree </h1>
      
      <div className={styles.description}>
        <h2>Description</h2>
        <p>
          A <b>Binary Tree</b> is a hierarchical structure where each node has at most two children —
          a left and a right child. This visualization shows how different traversal algorithms
          visit each node in a specific order.
        </p>
      </div>

      <div className={styles.useCase}>
        <h2>Use Cases</h2>
        <ul>
          <li><b>Preorder:</b> Used to create copies of trees or serialize them. <b>Root → Left → Right</b></li>
          <li><b>Inorder:</b> Produces sorted output for Binary Search Trees (BSTs). <b>Left → Root → Right</b></li>
          <li><b>Postorder:</b> Useful for deleting or freeing tree structures. <b>Left → Right → Root</b></li>
          <li><b>Level Order:</b> Ideal for shortest-path searches like BFS. <b>Breadth-first traversal</b></li>
        </ul>
      </div>

      <div className={styles.complexity}>
        <h2>Time Complexity</h2>
        <ul>
          <li><b>Traversal:</b> O(n)</li>
          <li><b>Insertion:</b> O(n)</li>
          <li><b>Deletion:</b> O(n)</li>
          <li><b>Space:</b> O(h) (where h is the tree height)</li>
        </ul>
      </div>

      <div className={styles.controls}>
        <button onClick={handleGenerate}>Generate Tree</button>
        <button onClick={() => handleTraversal("preorder")}>Preorder</button>
        <button onClick={() => handleTraversal("inorder")}>Inorder</button>
        <button onClick={() => handleTraversal("postorder")}>Postorder</button>
        <button onClick={() => handleTraversal("levelorder")}>Level Order (BFS)</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.vizContainer}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <BinaryTreeEngine
            key={key}
            traversal={traversal}
            setHighlightLine={setHighlightLine}
            highlightedNodes={highlightedNodes}
            setHighlightedNodes={setHighlightedNodes}
            setTraversalComplete={setTraversalComplete}
            resetTrigger={resetTrigger}
          />
          
       {traversal && (
          <div className={styles.resultBox}>
            <h3>Traversal Result ({traversal}):</h3>
            <p>
              {highlightedNodes.length === 0
                ? ""
                : ` Result: ${highlightedNodes.join(" → ")}`}
            </p>
          </div>
        )}
        </div>
      </div>

      <div className={styles.pseudoContainer}>
        <h3>Pseudocode</h3>

        {traversal === "preorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function preorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  visit(node)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  preorder(node.left)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  preorder(node.right)</span>
            </code>
          </pre>
        )}

        {traversal === "inorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function inorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  inorder(node.left)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  visit(node)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  inorder(node.right)</span>
            </code>
          </pre>
        )}

        {traversal === "postorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function postorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  postorder(node.left)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  postorder(node.right)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  visit(node)</span>
            </code>
          </pre>
        )}

        {traversal === "levelorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function levelOrder(root):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if root is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  queue = [root]</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  while queue not empty:</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>    node = queue.dequeue()</span>{"\n"}
              <span className={highlightLine === 5 ? styles.highlightCode : ""}>    visit(node)</span>{"\n"}
              <span className={highlightLine === 6 ? styles.highlightCode : ""}>    if node.left: queue.enqueue(node.left)</span>{"\n"}
              <span className={highlightLine === 7 ? styles.highlightCode : ""}>    if node.right: queue.enqueue(node.right)</span>
            </code>
          </pre>
        )}
      </div>
    </div>
  );
};

export default BinaryTreeWrapper;